import React from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";
import { cn } from "../../../../../utils/cn";
import { AGENT_ASSISTANT_NAME_MAX } from "../agentPayload";
import { CONTROL_CLS, SectionTitle, STYLE_FIELDS } from "./primitives";

// Personalización (capa 1): cómo escribe el agente. Se aplica ADEMÁS del
// prompt; el backend compila estas perillas al system prompt final.
//
// `assistant_name` es la firma de los mensajes de la IA: el backend antepone
// "*<nombre>:*" a cada respuesta, igual que firma el equipo. Vacío = sin firma.

export default function StyleSection({ value, onChange, errors = {}, assistantNameRef }) {
  const { t } = useTranslation();

  return (
    <section className="space-y-4">
      <SectionTitle>{t("Personalización")}</SectionTitle>
      <p className="text-xs text-fg-muted">
        {t("Define cómo escribe el asistente. Se aplica además de tus instrucciones.")}
      </p>

      <div className="space-y-1">
        <label
          htmlFor="agent-assistant-name"
          className="hd-field-label block text-xs font-medium text-fg"
        >
          {t("Nombre de la asistente")}
        </label>
        <input
          id="agent-assistant-name"
          ref={assistantNameRef}
          type="text"
          value={value.assistant_name ?? ""}
          maxLength={AGENT_ASSISTANT_NAME_MAX}
          onChange={(e) => onChange({ assistant_name: e.target.value })}
          placeholder={t("Ej.: Claudia")}
          aria-invalid={!!errors.assistant_name}
          aria-describedby="agent-assistant-name-hint"
          className={cn(CONTROL_CLS, "sm:!w-72")}
        />
        {errors.assistant_name ? (
          <p
            id="agent-assistant-name-hint"
            className="text-[12px] md:text-[11px] text-status-error"
            role="alert"
          >
            {t(errors.assistant_name)}
          </p>
        ) : (
          <p id="agent-assistant-name-hint" className="text-[12px] md:text-[11px] text-fg-muted">
            {t(
              "Firma de los mensajes de la IA, igual que firma el equipo (ej.: *Claudia:*). Déjalo vacío para no firmar.",
            )}
          </p>
        )}
      </div>

      <div className="divide-y divide-border">
        {STYLE_FIELDS.map((f) => (
          <div
            key={f.key}
            // En móvil el texto y el select se apilan: la columna de texto
            // quedaba en 134px contra un select de ancho fijo.
            className="flex flex-col items-stretch gap-1.5 md:flex-row md:items-center md:justify-between md:gap-4 py-2.5 first:pt-0 last:pb-0"
          >
            <div className="min-w-0">
              <label
                htmlFor={`agent-${f.key}`}
                className="hd-field-label block text-xs font-medium text-fg"
              >
                {t(f.label)}
              </label>
              <p className="text-[12px] md:text-[11px] text-fg-muted">{t(f.desc)}</p>
            </div>
            <div className="relative shrink-0 w-full md:w-auto">
              <select
                id={`agent-${f.key}`}
                value={value[f.key]}
                onChange={(e) => onChange({ [f.key]: e.target.value })}
                className={cn(CONTROL_CLS, "!w-full md:!w-36 appearance-none !pr-8")}
              >
                {f.options.map(([optionValue, label]) => (
                  <option key={optionValue} value={optionValue}>
                    {t(label)}
                  </option>
                ))}
              </select>
              <ChevronDown
                className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-muted pointer-events-none"
                aria-hidden="true"
              />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
